import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import lodashGet from "lodash/get";

// jaza-soft
import { showNotification } from "jazasoft";

import { getDistinctValues, isEmpty, isTrue } from "../../../../utils/helpers";

import useMutation from "../../../../hooks/useMutation";

import { useDeepMemo, useSetting } from "../../../../hooks";
import { QcStatus, SyncStatus } from "../../../../utils/types";
import SettingKeys from "../../../../utils/SettingKeys";
import { LotType, OrderFlowType } from "../../../../constants";
import { sanitizeRoll } from "../../../../helpers/warehouseHelper";

const predefinedRollFields = [
  "supplierRollNo",
  "factoryRollNo",
  "length",
  "weight",
  "width",
  "finishedWidth",
  "gsm",
  "rackNo",
  "qcStatus",
  "invStatus",
  "finishedWidth",
  "warpShrPercent",
  "weftShrPercent",
  "warpShrValue",
  "weftShrValue",
  "warpPattern",
  "weftPattern",
  "patternNo",
  "shade",
  "csv",
  "consignment",
  "skewness",
  "skewnessValue",
  "userRemarks"
];

const sanitizeRollInfo = ({
  id,
  rollNo,
  supplierRollNo,
  qcStatus,
  weight,
  length,
  width,
  finishedWidth,
  gsm,
  shade,
  csv,
  warpShrValue,
  weftShrValue,
  warpShrPercent,
  weftShrPercent,
  skewnessValue,
  warpPattern,
  weftPattern,
  patternNo,
  skewness,
  consignment,
  syncStatus,
  userRemarks
}) => ({
  id,
  rollNo,
  supplierRollNo,
  qcStatus,
  weight,
  length,
  width,
  finishedWidth,
  gsm,
  shade,
  csv,
  warpShrValue,
  weftShrValue,
  warpShrPercent,
  weftShrPercent,
  skewnessValue,
  warpPattern,
  weftPattern,
  patternNo,
  skewness,
  consignment,
  syncStatus,
  userRemarks
});

const useRollInventory = props => {
  const { order, selectedRow, effPartName, ledgerList, rollList, onRefresh } = props;

  const [tabIdx, setTabIdx] = useState(0);
  const [dialogState, setDialogState] = useState({ active: false, name: null, filename: null, flowInfo: null });
  const [drawerState, setDrawerState] = useState({ open: false, selectedRolls: [], name: null }); // name: 'split' | 'allocation'
  const [errorList, setErrorList] = useState([]);

  const dispatch = useDispatch();

  const [mutate] = useMutation();
  const [download] = useMutation({ rawResponse: true });
  const i18nMessages = useSelector(state => state?.i18n?.messages);

  const rollFields = useSetting(SettingKeys.ENTITY_FIELDS, "rolls");

  const fabricItemIds = getDistinctValues(ledgerList?.map(e => e.fabricItem?.id).filter(id => !isEmpty(id)));

  const rows = useDeepMemo(() => {
    if (isEmpty(rollList)) {
      return [];
    }
    const allocOrderIds = order?.orderFlowList?.filter(of => of.type === OrderFlowType.ALLOCATION)?.map(e => e.id) || [];
    const allocatedRollList = rollList
      .filter(r => !isEmpty(r.cutOrderId) || !isEmpty(r.groupOrderId) || !isEmpty(r.allocOrderId))
      .filter(r => allocOrderIds.includes(r.allocOrderId))
      .filter(r => r.style === selectedRow?.style && r.color === selectedRow?.color && r.part === effPartName);
    const unallocatedRollList = rollList?.filter(r => isEmpty(r.allocOrderId) && isEmpty(r.groupOrderId) && isEmpty(r.cutOrderId)) || [];

    let rows = [];
    if (tabIdx === 0 && !isEmpty(allocatedRollList)) {
      rows = allocatedRollList
        .map(roll => {
          const allocOrder = order?.orderFlowList?.find(e => e.id === roll.allocOrderId);
          const groupOrder = order?.orderFlowList?.find(e => e.id === roll.groupOrderId);
          const cutOrder = order?.orderFlowList?.find(e => e.id === roll.cutOrderId);
          return {
            ...roll,
            allocOrder,
            groupStateValue: groupOrder?.stateValue || null,
            cutOrderFlow: cutOrder,
            lotType: groupOrder?.lotType || cutOrder?.lotType,
            lot: groupOrder?.lot || cutOrder?.lot
          };
        })
        .map(({ parentId, ...roll }) => ({
          ...roll,
          lotValue: roll.lotType === LotType.BULK ? `Lot ${roll.lot}` : `${roll.lotType} ${roll.lot}`,
          invoiceNo: roll.ledgerIn?.invoice?.invoiceNo,
          millLength: roll.supplierRoll?.supplierLength,
          availableLength: roll.length,
          syncStatus: roll.extQcStatus === "Completed" && roll.version === roll.syncedVersion ? SyncStatus.IN_SYNC : roll.extQcStatus
        }))
        .sort((a, b) => (a.lotValue === b.lotValue ? (a.rollNo < b.rollNo ? -1 : 1) : a.lotValue < b.lotValue ? 1 : -1));
    } else {
      rows = unallocatedRollList
        .map(({ parentId, ...roll }) => ({
          ...roll,
          invoiceNo: roll.ledgerIn?.invoice?.invoiceNo,
          millLength: roll.supplierRoll?.supplierLength,
          availableLength: roll.length,
          syncStatus: roll.extQcStatus === "Completed" && roll.version === roll.syncedVersion ? SyncStatus.IN_SYNC : roll.extQcStatus
        }))
        .sort((a, b) => (a.rollNo < b.rollNo ? -1 : 1));
    }
    return rows;
  }, [order, rollList, effPartName, tabIdx, selectedRow]);

  const onValidate = values => {
    const rows = values.rows.map(row => ({
      qcStatus:
        row.qcStatus === QcStatus.PASS && (isEmpty(row.width) || isEmpty(row.shade))
          ? "QC can be passed after ['width', 'shade'] values are available"
          : undefined
    }));
    return { rows };
  };

  const onSubmit = (rows, _, onSubmitComplete) => {
    if (isEmpty(rows)) return;

    const fabricItemIds = getDistinctValues(ledgerList?.map(e => e.fabricItem?.id).filter(id => !isEmpty(id)));

    const options = {
      url: `rolls`,
      method: "put",
      data: rows.map(sanitizeRollInfo),
      params: { action: "default", fabricItemIds: fabricItemIds.join(",") }
    };

    mutate(options, {
      onSuccess: () => {
        onSubmitComplete && onSubmitComplete(rows);
        onRefresh && onRefresh();
      },
      onFailure: err => {
        if (err.status === 400 && err.errors && Array.isArray(err.errors)) {
          dispatch(showNotification(err.errors));
        }
      }
    });
  };

  const onDialogSubmit = values => {
    // Read Excel //
    const data = new FormData();
    data.append("file", values.file?.rawFile);
    const options = { url: `import/excel`, method: "post", data };
    mutate(options, {
      onSuccess: excelSheetList => {
        if (Array.isArray(excelSheetList) && excelSheetList.length > 0) {
          const excelRowList = excelSheetList[0].excelData?.rowList || [];
          const headerRow = excelRowList[0];
          const headerMap = headerRow.cellList.reduce(
            (acc, c) => ({
              ...acc,
              [`${c.colNo}`]: !isEmpty(c.value) ? `${c.value}`.replaceAll("\n", "").trim() : c.value?.replaceAll("\n", "") || ""
            }),
            {}
          );

          const labelDataKeyMap = predefinedRollFields.reduce(
            (acc, field) => ({
              ...acc,
              [lodashGet(i18nMessages, `resources.rolls.fields.${field}`, field).replaceAll("\n", "").toUpperCase()]: field
            }),
            {}
          );

          const rollList = excelRowList
            .slice(1)
            .map(row => row.cellList.reduce((acc, c) => ({ ...acc, [labelDataKeyMap[headerMap[`${c.colNo}`]?.toUpperCase()]]: c.value }), {}))
            .filter(r => !isEmpty(r.factoryRollNo) || !isEmpty(r.supplierRollNo));
          updateRollByExcel(rollList, dialogState.name);
        } else {
          setDialogState({ active: false });
        }
      },
      onFailure: err => {
        setDialogState({ active: false });
      }
    });
  };

  const updateRollByExcel = (rollInfoList, action) => {
    const hasFactoryRollNo = rollInfoList.some(ri => !isEmpty(ri.factoryRollNo));
    let finalRollList = [];
    if (hasFactoryRollNo) {
      finalRollList = rollInfoList
        .map(ri => ({
          ...ri,
          id: rows.find(e => `${e.rollNo}` === `${ri.factoryRollNo}`)?.id,
          rollNo: rows.find(e => `${e.rollNo}` === `${ri.factoryRollNo}`)?.rollNo
        }))
        .filter(ri => !isEmpty(ri.id));
    } else {
      finalRollList = rollInfoList
        .map(ri => ({
          ...ri,
          id: rows.filter(e => `${e.supplierRollNo}` === `${ri.supplierRollNo}`)[0]?.id,
          rollNo: rows.filter(e => `${e.supplierRollNo}` === `${ri.supplierRollNo}`)[0]?.rollNo,
          count: rows.filter(e => `${e.supplierRollNo}` === `${ri.supplierRollNo}`)?.length
        }))
        .filter(ri => !isEmpty(ri.id));
      if (finalRollList.some(ri => ri.count > 1)) {
        dispatch(
          showNotification(
            "GRN Packing List excel cannot be used for updating QC because supplier roll number is not unique in this order. Hint: Download excel from this page and update QC",
            { type: "danger" }
          )
        );
        setDialogState({ active: false });
        return;
      }
    }

    let options;
    if (action === "updateLength" || action === "updateQc") {
      options = {
        url: `rolls`,
        method: "PUT",
        params: { fabricItemIds: fabricItemIds.join(","), operation: action === "updateLength" ? "length" : "qc" },
        data: finalRollList
      };
    } else {
      const orderId = selectedRow.orderId;
      const productId = selectedRow.product?.id;
      const style = selectedRow.style;
      const color = selectedRow.color;

      options = {
        url: `rolls/allocate`,
        method: "put",
        params: { orderId, productId, style, color, part: effPartName },
        data: finalRollList
      };
    }

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification(`${dialogState.name === "updateQc" ? "QC updated " : "Stock updated"} successfully`));
        setDialogState({ active: false });
        onRefresh && onRefresh();
      },
      onFailure: err => {
        if (err.status === 400 && err.errors && Array.isArray(err.errors)) {
          setErrorList(err.errors.sort((a, b) => a.row - b.row));
        }
        setDialogState({ active: false });
      }
    });
  };

  const onSelectActionClick = (event, action, selectedRows, onActionComplete) => {
    if (isEmpty(selectedRow)) return [];
    const orderId = selectedRow.orderId;
    const productId = selectedRow.product?.id;
    const style = selectedRow.style;
    const color = selectedRow.color;
    const part = selectedRow.effPartName;

    if (action === "allocate") {
      setDrawerState({ open: true, selectedRolls: selectedRows, action, part });
    }
    if (action === "deallocate") {
      const hasCutOrder = selectedRows.some(row => !isEmpty(row.cutOrderFlow));
      const hasAllocOrder = selectedRows.some(row => !isEmpty(row.allocOrderId));
      const hasGroupOrder = selectedRows.some(row => !isEmpty(row.groupOrderId));

      if (hasCutOrder || (hasGroupOrder && hasAllocOrder)) {
        setDrawerState({ open: true, selectedRolls: selectedRows, action, part });
      } else {
        // eslint-disable-next-line no-restricted-globals
        if (confirm("Are you sure you want to deallocate these rolls. Rolls will be moved to unallocated")) {
          const options = {
            url: `rolls/${action}`,
            method: "put",
            params: { orderId, productId, style, color, part: effPartName },
            data: selectedRows.map(roll => ({
              id: roll.id,
              rollNo: roll.rollNo,
              supplierRollNo: roll.supplierRollNo,
              allocOrderId: null,
              groupOrderId: null,
              cutOrderId: null
            }))
          };
          mutate(options, {
            onSuccess: () => {
              dispatch(showNotification(`Rolls ${action}d successfully.`));
              onRefresh && onRefresh();
              onActionComplete && onActionComplete(rows);
            },
            onFailure: () => onActionComplete && onActionComplete(rows)
          });
        }
      }
    } else if (action === "split") {
      setDrawerState({ open: true, selectedRolls: selectedRows, action });
      onActionComplete && onActionComplete(rows);
    } else if (action === "merge") {
      if (selectedRows?.length <= 1) {
        dispatch(showNotification(`At least two split rolls must be selected for merging`, { type: "danger" }));
        onActionComplete && onActionComplete(rows);
        return;
      }

      const rollNumList = getDistinctValues(selectedRows.map(row => row.originalFactoryRollNo));
      if (rollNumList?.length > 1) {
        dispatch(showNotification(`Different roll numbers cannot be merged.`, { type: "danger" }));
        onActionComplete && onActionComplete(rows);
        return;
      }

      const options = {
        url: `rolls/splitting`,
        method: "put",
        data: selectedRows.map(sanitizeRoll),
        params: { action: "merge", fabricItemIds: fabricItemIds.join(",") }
      };

      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification(`Rolls ${action}d successfully.`));
          onRefresh && onRefresh();
          onActionComplete && onActionComplete(rows);
        },
        onFailure: () => onActionComplete && onActionComplete(rows)
      });
    } else if (action === "sync") {
      if (selectedRows.filter(e => e.qcStatus === QcStatus.PASS || e.qcStatus === QcStatus.FAIL)?.length < selectedRows?.length) {
        dispatch(showNotification(`Only rolls which are Pass/Fail can be synced. Hint: Select only rolls which are Pass/Fail`, { type: "danger" }));
        onActionComplete && onActionComplete(rows);
        return;
      }
      const syncableRolls = selectedRows.filter(e => isTrue(e.isSplit) || ["Pending", "Re-QC"].includes(e.extQcStatus));
      if (syncableRolls?.length < selectedRows?.length) {
        dispatch(showNotification(`Already synced rolls cannot be synced again. Hint: Send Re-QC request from ERP`, { type: "danger" }));
        onActionComplete && onActionComplete(rows);
        return;
      }
      if (selectedRows.every(row => row.extQcStatus === "Completed")) {
        dispatch(showNotification(`Selected Rolls are already synced.`, { type: "danger" }));
        onActionComplete && onActionComplete(rows);
        return;
      }

      const options = {
        url: `external/rolls/sync-qc`,
        method: "post",
        data: selectedRows.map(({ id, rollNo, supplierRollNo }) => ({ id, rollNo, supplierRollNo }))
      };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification(`Rolls ${action}ed successfully.`));
          onRefresh && onRefresh();
          // onActionComplete && onActionComplete(rows);
        }
        // onFailure: () => onActionComplete && onActionComplete(rows)
      });
      onActionComplete && onActionComplete(rows);
    }
  };

  const onFooterActionClick = (event, action, rows, filterValues, onActionComplete) => {
    if (action === "updateLength" || action === "updateQc" || action === "allocate") {
      setDialogState({
        active: true,
        name: action,
        title: `${action === "updateLength" ? "Update Length" : action === "updateQc" ? "Update QC" : "Allocate"} by Excel`
      });
    } else if (action === "download") {
      const filename = `ROLLS`.replaceAll(" ", "-").replaceAll("/", "-");

      const isKnitwear = rows.some(row => !isEmpty(row.weight));
      const finalRollFields = [
        ...rollFields.filter(field => field !== "qcStatus").filter(field => isKnitwear || !["weight", "finishedWidth", "gsm"].includes(field)),
        "qcStatus"
      ].filter(field => predefinedRollFields.includes(field));
      const columnList = finalRollFields
        .map((field, idx) => ({
          dataKey: field,
          title: lodashGet(i18nMessages, `resources.rolls.fields.${field}`, field),
          colNo: idx + 1
        }))
        .map(c => ({ ...c, title: c.title?.toUpperCase() }));

      const rollIds = rows
        .filter(row => isEmpty(filterValues?.consignment) || filterValues?.consignment === row.consignment)
        .filter(row => isEmpty(filterValues?.invoiceNo) || filterValues?.invoiceNo === row.invoiceNo)
        .filter(row => isEmpty(filterValues?.lotValue) || filterValues?.lotValue === row.lotValue)
        .filter(row => isEmpty(filterValues?.shade) || filterValues?.shade === row.shade)
        .filter(row => isEmpty(filterValues?.csv) || filterValues?.csv === row.csv)
        .filter(row => isEmpty(filterValues?.qcStatus) || filterValues?.qcStatus === row.qcStatus)
        .map(row => row.id);

      const rowList = rollList
        .filter(roll => rollIds.includes(roll.id))
        .sort((a, b) => a.id - b.id)
        .map(r => ({ ...r, supplierLength: r.supplierRoll?.supplierLength }))
        .map((row, idx) => ({
          rowNo: idx + 1,
          cellList: columnList.map(c => ({
            dataKey: c.dataKey,
            value: row[c.dataKey]
          }))
        }));
      const options = {
        url: `export/excel`,
        method: "post",
        responseType: "arraybuffer",
        params: { filename },
        data: [{ sheetNo: 0, sheetName: "Rolls", excelData: { columnList, rowList } }]
      };

      download(options, {
        onSuccess: response => {
          if (response.status === 200) {
            const header = response.headers["content-disposition"];
            var fileName = header ? header.match(/filename="(.+)"/)[1] : `${filename}.xlsx`;

            let blob = new Blob([response.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
          }
          onActionComplete && onActionComplete(rows);
        }
      });
    }
    onActionComplete && onActionComplete(rows);
  };

  return {
    rollFields,
    rollList,
    rows,
    tabIdx,
    dialogState,
    drawerState,
    errorList,
    onValidate,
    onSubmit,
    onDialogSubmit,
    onSelectActionClick,
    onFooterActionClick,
    setTabIdx,
    setDialogState,
    setDrawerState,
    setErrorList
  };
};

export default useRollInventory;
